import { z } from 'zod'
import { ALL_SCOPES, ROLES } from '../constants'

export const loginCallbackSchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
})

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1).optional(), // optional if sent via cookie
})

/** Zod schema for a single path scope entry (GH-112) */
export const pathScopeSchema = z.object({
  folderId: z.string().min(1, 'folderId must be a non-empty string'),
  includeChildren: z.boolean().default(true),
})

export type PathScopeInput = z.infer<typeof pathScopeSchema>

/** Cap per-key path scope count to prevent DoS via excessive folder lookups */
const MAX_PATH_SCOPES = 50

export const createApiKeySchema = z.object({
  name: z.string().min(1).max(100),
  scopes: z.array(
    z.string().refine((s) => (ALL_SCOPES as readonly string[]).includes(s), { message: 'Invalid scope' }),
  ).min(1),
  expiresInDays: z.number().int().min(1).max(365).optional(),
  /** null = unrestricted; omit = unrestricted; array = path-scoped (GH-112) */
  pathScopes: z.array(pathScopeSchema).max(MAX_PATH_SCOPES).nullable().optional(),
})

export const updateApiKeySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  scopes: z.array(
    z.string().refine((s) => (ALL_SCOPES as readonly string[]).includes(s), { message: 'Invalid scope' }),
  ).min(1).optional(),
  expiresInDays: z.number().int().min(1).max(365).optional().nullable(),
  /** null = unrestricted; omit = no change; array = path-scoped (GH-112) */
  pathScopes: z.array(pathScopeSchema).max(MAX_PATH_SCOPES).nullable().optional(),
}).refine((d) => d.name !== undefined || d.scopes !== undefined || d.expiresInDays !== undefined || d.pathScopes !== undefined, { // null is valid for expiresInDays (removes expiry)
  message: 'At least one field must be provided',
})

export const inviteUserSchema = z.object({
  email: z.string().email(),
  role: z.enum(ROLES),
})

export const updateMemberRoleSchema = z.object({
  role: z.enum(ROLES),
})

export const upsertStorageSettingsSchema = z.object({
  accountId: z.string().min(1).max(200),
  accessKey: z.string().min(1).max(500),
  secretKey: z.string().min(1).max(500),
  bucketName: z.string().min(1).max(200),
  endpointUrl: z.string().url().max(500).optional().nullable(),
})
